/* eslint-disable max-lines */
import styled, { css, keyframes } from "styled-components";
import { TICK_ACCENT, TICK_STATES } from "../../constants";

const rotate = keyframes`
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
`;

const motion = keyframes`
    0% {
        stroke-dasharray: 1, 351;
        stroke-dashoffset: 0;
    }
    50% {
        stroke-dasharray: 251, 351;
        stroke-dashoffset: -50;
    }
    100% {
        stroke-dasharray: 251, 351;
        stroke-dashoffset: -301;
    }
`;

const breathe = keyframes`
    0% {
        transform: scale(1);
    }
    48% {
        transform: scale(1.2);
    }
    50% {
        transform: scale(1.2);
    }
    100% {
        transform: scale(1);
    }
`;

const breatheOverflow = keyframes`
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.2);
    }
    80% {
        opacity: 1;
    }
    100% {
        transform: scale(1.6);
        opacity: 0;
    }
`;

const fillUp = keyframes`
    to {
        opacity: 1;
        stroke-dashoffset: 0;
    }
`;

const easein = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const bounce = keyframes`
    0% {
        opacity: 0;
        transform: translateX(-200px);
    }
    20% {
        opacity: 1;
        transform: translateX(30px);
    }
    40% {
        transform: translateX(0px);
    }
    80% {
        opacity: 1;
        transform: translateX(400px);
    }
    90% {
        opacity: 1;
        transform: translateX(500px);
    }
    100% {
        opacity: 0;
        transform: translateX(600px);
    }
`;

interface TickScreenStyleProps {
    $tickState?: TICK_STATES;
    $tickAccent?: TICK_ACCENT;
    $isSuper?: boolean;
    $isSwiggyMoney?: boolean;
    $showLoadingScreen?: boolean;
    $showTickScreen?: boolean;
    $isJioHotstarFlow?: boolean;
}

export const Container = styled.div<TickScreenStyleProps>`
    display: flex;
    height: 100vh;
    width: 100%;
    overflow: hidden;
    flex-direction: column;
    justify-content: center;
    background: ${(props) => props.theme.colors[props.theme.colorMode].background_Primary};
    position: relative;
    margin-top: ${(props) => (props.$isJioHotstarFlow ? "50px" : "0")};
`;

export const Wrapper = styled.div`
    display: flex;
    width: 100%;
    overflow: visible;
    flex-direction: column;
    justify-content: center;
`;

export const TickSvg = styled.svg<TickScreenStyleProps>`
    transform-origin: center center;
    margin: auto;
    display: block;
    transform: translateZ(0);
    width: 140px;
    height: 140px;
    overflow: visible !important;

    ${(props) =>
        props.$showLoadingScreen &&
        css`
            animation: ${rotate} 2.2s linear infinite;
            transform: rotate(360deg);
            will-change: transform;
        `}
`;

export const CircleWrapper = styled.circle<TickScreenStyleProps>`
    fill: none;
    stroke-width: 10px;

    ${(props) => {
        const { theme, $tickAccent, $showTickScreen, $isSuper, $isSwiggyMoney } = props;
        let strokeColor = theme.colors[theme.colorMode].primary_Sub;

        if ($isSuper) {
            strokeColor = theme.colors[theme.colorMode].primary_Sub;
        } else if ($isSwiggyMoney || $tickAccent === TICK_ACCENT.GREEN) {
            strokeColor = theme.colors[theme.colorMode].background_Primary;
        } else if ($tickAccent === TICK_ACCENT.VIOLET) {
            strokeColor = theme.colors[theme.colorMode].rain_Sub;
        } else if ($tickAccent === TICK_ACCENT.BRICK) {
            strokeColor = theme.colors[theme.colorMode].background_Primary;
        } else if ($tickAccent === TICK_ACCENT.MUSTARD) {
            strokeColor = theme.colors[theme.colorMode].highlight_Sub;
        } else if ($tickAccent === TICK_ACCENT.LIGHT_GREEN) {
            strokeColor = theme.colors[theme.colorMode].background_Primary;
        }

        return css`
            stroke: ${strokeColor};

            ${$showTickScreen &&
            css`
                fill: ${strokeColor};
                stroke-width: 0;
                transform-origin: center;
                animation: ${breatheOverflow} 800ms ease-in-out;
            `}
        `;
    }}
`;

export const Circle = styled.circle<TickScreenStyleProps>`
    fill: none;
    stroke-width: 10px;

    ${(props) => {
        const { theme, $tickAccent, $showTickScreen, $showLoadingScreen, $isSuper, $isSwiggyMoney } = props;
        let strokeColor = theme.colors[theme.colorMode].primary;

        if ($isSuper) {
            strokeColor = theme.colors[theme.colorMode].primary;
        } else if ($isSwiggyMoney || $tickAccent === TICK_ACCENT.GREEN) {
            strokeColor = theme.colors[theme.colorMode].special_positive;
        } else if ($tickAccent === TICK_ACCENT.VIOLET) {
            strokeColor = theme.colors[theme.colorMode].rain;
        } else if ($tickAccent === TICK_ACCENT.BRICK) {
            strokeColor = theme.colors[theme.colorMode].swiggy_One;
        } else if ($tickAccent === TICK_ACCENT.MUSTARD) {
            strokeColor = theme.colors[theme.colorMode].highlight;
        } else if ($tickAccent === TICK_ACCENT.LIGHT_GREEN) {
            strokeColor = theme.colors[theme.colorMode].positive;
        }

        return css`
            stroke: ${strokeColor};

            ${$showLoadingScreen &&
            css`
                animation: ${motion} 1.25s linear infinite;
                stroke-linecap: round;
                transform: translateZ(0);
                will-change: transform;
            `}

            ${$showTickScreen &&
            css`
                fill: ${strokeColor};
                animation: ${breathe} 800ms ease-in-out;
                transform-origin: center;
            `}
        `;
    }}
`;

export const TickPath = styled.path<TickScreenStyleProps>`
    stroke: ${(props) => props.theme.colors[props.theme.colorMode].background_Primary};
    fill: transparent;
    stroke-width: 10px;
    stroke-dasharray: 140px;
    stroke-dashoffset: 140px;
    transition: stroke-dashoffset 1.5s ease-out;
    stroke-linecap: round;
    stroke-linejoin: round;

    ${(props) =>
        props.$showTickScreen &&
        css`
            stroke-dasharray: 1000;
            stroke-dashoffset: 1000;
            animation: ${fillUp} 1.5s ease-in-out;
            animation-delay: 200ms;
            animation-fill-mode: forwards;
        `}

    ${(props) =>
        props.$isSuper &&
        css`
            stroke: ${props.theme.colors[props.theme.colorMode].background_Primary};
        `}

    ${(props) =>
        props.$isSwiggyMoney &&
        css`
            stroke: ${props.theme.colors[props.theme.colorMode].background_Primary};
        `}
`;

export const TickShadowPath = styled.path`
    stroke: ${(props) => props.theme.colors[props.theme.colorMode].background_Primary};
    stroke-opacity: 0.4;
    fill: transparent;
    stroke-width: 10px;
    stroke-dasharray: 140px;
    stroke-dashoffset: 0;
    stroke-linecap: round;
    stroke-linejoin: round;
`;

export const InfoContainer = styled.div<TickScreenStyleProps>`
    margin: ${(props) => (props.$isJioHotstarFlow ? "20px 16px 40px" : "34px 16px 40px")};
    height: 100px;

    ${(props) =>
        props.$showLoadingScreen &&
        css`
            opacity: 1;
            transition: opacity 0.4s ease-out;
        `}

    ${(props) =>
        props.$showTickScreen &&
        css`
            text-align: center;
            font-size: 15px;
            opacity: 1;
            transform: translate3d(0, 0, 0);
            will-change: opacity, transform;
            animation: ${easein} 0.2s linear;
        `}

    ${(props) =>
        props.$isSuper &&
        props.$showLoadingScreen &&
        css`
            color: ${props.theme.colors[props.theme.colorMode].text_Med_Emphasis};
        `}

    ${(props) =>
        props.$isSwiggyMoney &&
        props.$showLoadingScreen &&
        css`
            color: ${props.theme.colors[props.theme.colorMode].text_Highest_Emphasis};
        `}
`;

export const MainTextContainer = styled.div<TickScreenStyleProps>`
    ${(props) =>
        props.$showLoadingScreen &&
        css`
            text-align: center;
            margin: ${props.$isJioHotstarFlow ? "10px 0 6px 0" : "10px 0"};
        `}

    ${(props) =>
        props.$showTickScreen &&
        css`
            margin: 15px 0;
        `}
        
    ${(props) =>
        props.$isJioHotstarFlow &&
        css`
            margin-bottom: 4px;
        `}

    ${(props) =>
        props.$isSwiggyMoney &&
        props.$showTickScreen &&
        css`
            color: ${props.theme.colors[props.theme.colorMode].text_Highest_Emphasis} !important;
            font-size: 20px;
        `}
`;

export const SubTextContainer = styled.div<TickScreenStyleProps>`
    ${(props) =>
        props.$showLoadingScreen &&
        css`
            text-align: center;
        `}

    ${(props) =>
        props.$showTickScreen &&
        css`
            margin: 0 10%;
        `}

    ${(props) =>
        props.$isSwiggyMoney &&
        props.$showTickScreen &&
        css`
            font-weight: 200 !important;
            font-size: 13px;
            color: ${props.theme.colors[props.theme.colorMode].text_Med_Emphasis} !important;
        `}
`;

export const SubTextChange = styled.div`
    opacity: 1;
    transform: translate3d(0, 0, 0);
    will-change: opacity, transform;
    animation: ${easein} 0.6s linear;
`;

export const IntermediateTextContainer = styled.div<TickScreenStyleProps>`
    margin-bottom: 4px;

    ${(props) =>
        props.$showLoadingScreen &&
        css`
            text-align: center;
        `}
`;

export const ScooterContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: center;
    position: absolute;
    bottom: 5%;
    animation-name: ${bounce};
    animation-fill-mode: both;
    animation-duration: 4s;
`;

export const BottomMessageContainer = styled.div`
    align-items: center;
    text-align: center;
    position: absolute;
    bottom: 5%;
    width: 100%;
`;

export const BottomMessageCardBrand = styled.div`
    display: flex;
    padding-bottom: 10px;
    justify-content: center;
`;

export const BottomMessageCardLogo = styled.div`
    padding-right: 7px;
`;

export const BottomMessageSecure = styled.div`
    display: flex;
    justify-content: center;
`;
